import { ScrollbarManager } from '../host-page-tools';
import { IFrameInitStyles } from './iframe-init-styles';
import { IFrameWithSettings } from './iframe-with-settings';

export class IFrameManager {
  constructor(private window: Window) {
    this.scrollbarManager = new ScrollbarManager(this.window);
  }

  private scrollbarManager: ScrollbarManager;
  private iframes: { [name: string]: IFrameWithSettings } = {};

  createFrame(name: string, url: string, iFrameInitStyles: IFrameInitStyles, onFrameLoad?: () => void): void {
    this.iframes[name] = { iframe: this.window.document.createElement('iframe'), initialStyles: iFrameInitStyles };
    const iframe = this.getIFrameOrThrow(name);

    iframe.src = url;
    iframe.name = name;
    iframe.allow = 'clipboard-write; payment';
    iframe.style.position = 'fixed';
    iframe.style.left = '0';
    iframe.style.top = '0';
    iframe.style.width = iFrameInitStyles.width;
    iframe.style.height = '100%';
    iframe.style.padding = '0';
    iframe.style.border = 'none';
    iframe.style.margin = '0';
    iframe.style.zIndex = iFrameInitStyles.zIndex;
    iframe.style.display = iFrameInitStyles.display;
    iframe.style.colorScheme = iFrameInitStyles.colorScheme;
    iframe.style.filter = iFrameInitStyles.filter ?? '';
    iframe.style.transition = iFrameInitStyles.transition ?? '';
    this.window.document.body.appendChild(iframe);

    if (onFrameLoad) {
      iframe.addEventListener('load', () => onFrameLoad());
    }
  }

  showFrame(name: string): void {
    const iframe = this.getIFrameOrThrow(name);
    iframe.style.display = 'block';
    iframe.focus();
    this.scrollbarManager.hideBodyScrollbars(name);
  }

  hideFrame(name: string): void {
    const iframe = this.getIFrameOrThrow(name);
    iframe.style.display = 'none';
    this.scrollbarManager.resetBodyScrollbars(name);
  }

  sendMessageToFrame<T>(name: string, message: T): void {
    const iframe = this.getIFrameOrThrow(name);
    if (iframe.contentWindow) {
      iframe.contentWindow.postMessage(message, iframe.src);
    } else {
      iframe.addEventListener('load', () => this.sendMessageToFrame(name, message));
    }
  }

  onReceiveMessageFromFrame<T>(name: string, expectedSource: string, handler: (message: T) => void): void {
    this.window.addEventListener('message', event => {
      if (event.origin === this.getHostname(name) && event.data.source === expectedSource) {
        handler(event.data);
      }
    });
  }

  setFrameWidth(name: string, width: string): void {
    const iframeWithSettings = this.getIFrameWithSettingsOrThrow(name);
    const isSettingToFullWidth: boolean = this.isSettingToFullWidth(width);

    if (isSettingToFullWidth || iframeWithSettings.iframe.style.width.startsWith('0')) {
      iframeWithSettings.iframe.style.transition = '';
    } else if (!isSettingToFullWidth) {
      iframeWithSettings.iframe.style.transition = iframeWithSettings.initialStyles.transition ?? '';
    }

    iframeWithSettings.iframe.style.width = width;

    if (isSettingToFullWidth && iframeWithSettings.iframe.style.display !== 'none') {
      this.scrollbarManager.hideBodyScrollbars(name);
    } else {
      this.scrollbarManager.resetBodyScrollbars(name);
    }
  }

  private isSettingToFullWidth(width: string): boolean {
    if (width.endsWith('px')) {
      return Number(width.split('px', 1)[0]) >= this.window.innerWidth;
    } else if (width.endsWith('%')) {
      return Number(width.split('%', 1)[0]) >= 100;
    } else {
      return false;
    }
  }

  private getHostname(name: string): string {
    const a = this.window.document.createElement('a');
    a.href = this.getIFrameOrThrow(name).src;
    return `${a.protocol}//${a.host}`;
  }

  private getIFrameWithSettingsOrThrow(name: string): IFrameWithSettings {
    if (this.iframes[name] !== undefined) {
      return this.iframes[name];
    } else {
      throw new Error('IFrame ' + name + ' not initialized');
    }
  }

  private getIFrameOrThrow(name: string): HTMLIFrameElement {
    return this.getIFrameWithSettingsOrThrow(name).iframe;
  }
}
